import React, { useEffect, useState } from 'react';
import './RoomLayoutEditor.css';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';
const CELL = 28;     // px per grid cell (matches RoomLayoutEditor)
const ICON_R = 14;   // radius of placed-product disc

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

function initials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).slice(0, 2);
  const out = words.map(w => w[0]).join('').toUpperCase();
  return out || name[0].toUpperCase();
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// Read-only preview of a room's saved floor-plan layout.
// Renders the same grid/outline/product markers as RoomLayoutEditor, but
// with no editing handles or interactivity. Scales to fit its container.
const RoomLayoutPreview = ({ roomId, onOpenLayout }) => {
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${CHAT_API_BASE}/room-layout.php?room_id=${roomId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.data && data.data.layout) {
          setLayout(data.data.layout);
        }
      } catch (e) {
        /* ignore — render empty state */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  if (loading) {
    return <div className="room-layout-preview loading">Loading layout…</div>;
  }

  const hasOutline = layout && Array.isArray(layout.outline) && layout.outline.length >= 3;
  const hasItems = layout && Array.isArray(layout.items) && layout.items.length > 0;
  const floorplan = layout && layout.floorplan && layout.floorplan.url ? layout.floorplan : null;

  if (!layout || (!hasOutline && !hasItems && !floorplan)) {
    return (
      <div className="room-layout-preview empty" onClick={onOpenLayout}>
        <span className="empty-icon">📐</span>
        <p>No layout yet.{onOpenLayout ? ' Click to open the Layout editor.' : ''}</p>
      </div>
    );
  }

  const cols = layout.grid?.cols || 30;
  const rows = layout.grid?.rows || 20;
  const widthPx = cols * CELL;
  const heightPx = rows * CELL;
  const polygonPoints = (layout.outline || []).map(p => `${p.x * CELL},${p.y * CELL}`).join(' ');

  const gridLines = [];
  for (let i = 0; i <= cols; i++) {
    gridLines.push(<line key={`v${i}`} x1={i * CELL} y1={0} x2={i * CELL} y2={heightPx} />);
  }
  for (let j = 0; j <= rows; j++) {
    gridLines.push(<line key={`h${j}`} x1={0} y1={j * CELL} x2={widthPx} y2={j * CELL} />);
  }

  return (
    <div
      className={`room-layout-preview ${onOpenLayout ? 'clickable' : ''}`}
      onClick={onOpenLayout}
      title={onOpenLayout ? 'Open the Layout editor' : undefined}
    >
      <svg
        className="layout-canvas"
        width="100%"
        viewBox={`0 0 ${widthPx} ${heightPx}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {floorplan && (
          <image
            className="layout-floorplan-img"
            href={floorplan.url}
            xlinkHref={floorplan.url}
            x={0}
            y={0}
            width={widthPx}
            height={heightPx}
            preserveAspectRatio="none"
          />
        )}
        {!floorplan && <g className="grid-lines">{gridLines}</g>}
        {hasOutline && (
          <polygon className="layout-polygon" points={polygonPoints} />
        )}
        {(layout.items || []).map(it => {
          // Mirror the editor: markers can be a circle or a rectangle, sized by
          // w/h (older markers without these default to the 28px circle).
          const shape = it.shape || 'circle';
          const w = it.w || ICON_R * 2;
          const h = it.h || ICON_R * 2;
          const halfW = w / 2;
          const halfH = shape === 'rect' ? h / 2 : halfW;
          return (
            <g
              key={it.id}
              className="layout-item"
              transform={`translate(${it.x * CELL}, ${it.y * CELL})`}
            >
              {shape === 'rect' ? (
                <rect x={-halfW} y={-halfH} width={w} height={h} rx="3"
                  fill={it.color} stroke="#1a1a1a" strokeWidth="1" />
              ) : (
                <circle r={halfW} fill={it.color} stroke="#1a1a1a" strokeWidth="1" />
              )}
              <text y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1a1a1a">
                {initials(it.label)}
              </text>
              <title>{it.label}</title>
              <text y={halfH + 12} textAnchor="middle" fontSize="10" fill="#ddd">
                {truncate(it.label, 18)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default RoomLayoutPreview;
